import type z from "zod";

/**
 * All possible keys for the DTO validator variants
 */
export enum DtoValidatorKeys {
    Base = "base",
    BaseLax = "baseLax",
    Create = "create",
    CreateLax = "createLax",
    Update = "update",
}

/** The variants that validate request input rather than stored documents. */
export const REQUEST_DTO_VARIANTS: ReadonlySet<DtoValidatorKeys> = new Set([
    DtoValidatorKeys.Create,
    DtoValidatorKeys.CreateLax,
    DtoValidatorKeys.Update,
]);

/**
 * Base structure for DTO validator objects used by all generated `dtoValidators` exports.
 */
export type DtoValidatorTypesBase = Record<DtoValidatorKeys, unknown>;

/**
 * Maps a schema's generated DTO object types to the corresponding Zod validator object.
 */
export type DtoValidatorTypes<T extends DtoValidatorTypesBase> = {
    [K in DtoValidatorKeys]: z.ZodType<T[K]>;
};

/**
 * Structural type for a constructor where the static `dtoValidators` is read.
 * This is not an instance shape; do not use `implements ValidatableClass` on a class that only declares
 * `static dtoValidators`
 *
 * Subclasses that want to participate in DTO validation should:
 *
 * 1. Declare `static dtoValidators: DtoValidatorTypes<RelativeSchemaTypes>;` with their generated
 *    schema types.
 * 2. Assign it after the class body via
 *    `ClassName.dtoValidators = getZodSchemaFromClass(ClassName) as unknown as DtoValidatorTypes<RelativeSchemaTypes>;`
 */
export interface ValidatableClass {
    // Present on real class constructors
    readonly name?: string;
    dtoValidators?: DtoValidatorTypes<DtoValidatorTypesBase>;
}

/**
 * Helper type that extracts the relative DTO type for the specified class and DTO variant
 */
export type RelativeDtoVariant<T extends ValidatableClass, V extends DtoValidatorKeys> = T extends {
    dtoValidators?: DtoValidatorTypes<infer D>;
}
    ? D[V]
    : never;

/**
 * Re-types a DTO validator as the `z.ZodObject` it actually is at runtime.
 *
 * `dtoValidators` entries are declared as the base `z.ZodType<DTO>` (see {@link DtoValidatorTypes}), which
 * hides ZodObject methods like `.pick()` / `.extend()`. The underlying value is always a ZodObject, so this
 * widening is safe — and because the DTO type is inferred from the schema, callers get a precisely typed
 * object schema for free instead of hand-writing the shape or falling back to `z.ZodObject<any>`.
 *
 * @example
 * const createSchema = asZodObject(CommandGroup.dtoValidators.create).extend({ commands: z.array(...) });
 */
export const asZodObject = <T>(schema: z.ZodType<T>) =>
    schema as unknown as z.ZodObject<{ [K in keyof T as K extends string ? K : never]: z.ZodType<T[K]> }>;
